import deleteCompanyTemplateApi from '@/api/delete/company/delete-company-template';
import getCompanyTemplatesApi from '@/api/get/company/company-template/get-company-templates';
import Alert from '@/components/alert/alert-component';
import ConfirmationModal from '@/components/confirmation-modal/confirmation-modal';
import PermissionWrapper from '@/components/permission-wrapper/permission-wrapper-component';
import StatusIndicator from '@/components/status-indicator/status-indicator-component';
import ViewEditDelete from '@/components/table/components/view-edit-delete/view-edit-delete';
import Table from '@/components/table/table';
import { useLazyQuery, useMutation } from '@/hooks/use-api';
import { useCallback, useRef, useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import Styled from './templates-all-style';
/**
 * Table of company templates
 * @returns {JSX.Element}
 */
const TemplatesAll = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const tableId = 'company-template-list';
  const [fetchingPresets, setFetchingPresets] = useState(true);
  const [defaultVariables, setDefaultVariables] = useState({});
  const [defaultSort, setDefaultSort] = useState('template.created_at:DESC');

  const renderStatusPill = useCallback(({ row }) => {
    const { status } = row.original;
    return <StatusIndicator status={status} />;
  }, []);

  const renderDate = useCallback(({ row }) => {
    if (row.original) {
      return (
        <>
          Modified {dayjs(row.original.updated_at).format('YYYY/MM/DD HH:mm')}
        </>
      );
    }
  }, []);
  const renderProductsLinked = useCallback(
    ({ row }) => {
      if (row.original) {
        return (
          <Link
            to={`/app/company-management/view/${id}/products`}
            state={{ templateId: row.original.id }}
          >
            {row.original.products_linked}
          </Link>
        );
      }
    },
    [id],
  );

  const [deleteCompanyTemplate, { loading: deleteCompanyTemplateLoading }] =
    useMutation(deleteCompanyTemplateApi);

  const getApi = useRef(getCompanyTemplatesApi(id));

  const {
    data,
    count,
    error,
    loading: isLoading,
    refetch,
  } = useLazyQuery(getApi.current);

  const getTablePresets = useCallback(() => {
    const tablePreferences = localStorage.getItem('tablePreferences');
    if (tablePreferences) {
      const tableConfigs = JSON.parse(tablePreferences)[tableId];

      if (tableConfigs) {
        const { query } = tableConfigs;

        if (query.length > 0) {
          // Get cached search value and apply it
          const searchQuery = query?.filter((q) => q.includes('search='));
          if (searchQuery?.length > 0) {
            const tempSearch = searchQuery[0].replace('search=', '');
            setDefaultVariables({ ...defaultVariables, search: tempSearch });
          }

          // Get cached sort value and apply it
          const sortQuery = query?.filter((q) => q.includes('sort='));
          if (sortQuery?.length > 0) {
            const tempSort = sortQuery[0].replace('sort=', '');
            setDefaultSort(tempSort);
          }

          // Get cached page size and apply it
          const pageSizeQuery = query?.filter((q) => q.includes('limit='));
          if (pageSizeQuery?.length > 0) {
            const tempPageSize = parseInt(
              pageSizeQuery[0].replace('limit=', ''),
              10,
            );
            setDefaultVariables({ ...defaultVariables, limit: tempPageSize });
          }
        }
      }
    }
    setFetchingPresets(false);

    // This is disabled because we don't want to re-run this function when the defaultVariables changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // USE EFFECTS
  useEffect(() => {
    getTablePresets();
  }, [getTablePresets]);

  const renderThumbnail = useCallback(
    ({ row }) =>
      row?.original?.thumbnail ? (
        <Styled.ImageContainer>
          <Styled.Thumbnail src={row?.original?.thumbnail} alt="thumbnail" />
        </Styled.ImageContainer>
      ) : (
        <Styled.IconContainer>
          <Styled.NoThumbnail />
        </Styled.IconContainer>
      ),
    [],
  );

  const launchDeleteConfirmation = useCallback(
    (TemplateData) => {
      ConfirmationModal({
        title: 'Delete Option',
        text: `Are you sure you want to delete ${TemplateData.values.name}? This will disassociate the template from ${TemplateData.values.products_linked} product(s).`,
        onConfirm: () =>
          deleteCompanyTemplate(id, TemplateData?.original?.id).then(() => {
            refetch().then(() => {
              Alert({
                icon: 'success',
                text: 'Template deleted successfully',
              });
            });
          }),
      });
    },
    [deleteCompanyTemplate, id, refetch],
  );

  const filters = [
    {
      header: 'Status:',
      accessor: 'status',
      placeholder: 'Please select a Status',
      type: 'select',
      options: [
        { value: 'active', text: 'Active' },
        { value: 'draft', text: 'Draft' },
        { value: 'invalid', text: 'Invalid' },
      ],
    },
    {
      header: 'Linked to Products:',
      accessor: 'products_linked',
      placeholder: 'Please select a Products',
      type: 'select',
      options: [
        { value: 'yes', text: 'Yes' },
        { value: 'no', text: 'No' },
      ],
    },
  ];

  const handleEdit = useCallback(
    (row) => {
      const templateDetails = row.original;
      navigate(`edit/${templateDetails.id}`);
    },
    [navigate],
  );

  const handleView = useCallback(
    (row) => {
      const templateDetails = row.original;
      navigate(`edit/${templateDetails.id}/preview`, {
        state: { fromTable: true },
      });
    },
    [navigate],
  );

  const handleDelete = useCallback(
    (row) => {
      launchDeleteConfirmation(row);
    },
    [launchDeleteConfirmation],
  );

  const handleDuplicate = useCallback(
    (row) => {
      const templateDetails = row.original;
      navigate(`new`, {
        state: { isTemplateDuplicate: templateDetails.id },
      });
    },
    [navigate],
  );

  const Actions = useCallback(
    ({ row }) => (
      <ViewEditDelete
        feature="template"
        onEdit={() => handleEdit(row)}
        onDelete={() => handleDelete(row)}
        onView={() => handleView(row)}
        viewText="Preview"
        onDuplicate={() => handleDuplicate(row)}
      />
    ),
    [handleDelete, handleDuplicate, handleEdit, handleView],
  );

  const TemplateColumns = [
    {
      Header: 'Thumbnail',
      id: 'thumbnail',
      width: 100,
      accessor: 'thumbnail',
      Cell: (props) => renderThumbnail(props),
    },
    {
      Header: 'NAME',
      id: 'name',
      accessor: 'name',
      search: true,
    },
    {
      Header: 'PART NUMBER',
      id: 'part_number',
      accessor: 'part_number',
      search: true,
    },
    {
      Header: 'CATEGORY',
      id: 'categories',
      accessor: 'categories',
      search: true,
    },
    {
      Header: 'PRODUCT CATEGORY',
      id: 'product_category',
      accessor: 'product_category',
      search: true,
    },
    {
      Header: 'PRODUCTS LINKED',
      id: 'products_linked',
      accessor: 'products_linked',
      Cell: (props) => renderProductsLinked(props),
    },
    {
      Header: 'DATE',
      id: 'updated_at',
      accessor: 'updated_at',
      search: true,
      Cell: (props) => renderDate(props),
    },
    {
      Header: 'STATUS',
      id: 'status',
      accessor: 'status',
      search: true,
      Cell: (props) => renderStatusPill(props),
    },
    {
      Header: 'ACTIONS',
      id: 'actions',
      Cell: (props) => Actions(props),
    },
  ];

  return (
    <Table
      feature="template"
      onRowEdit={(row) => handleEdit(row)}
      onRowDelete={(row) => handleDelete(row)}
      onRowDuplicate={(row) => handleDuplicate(row)}
      onRowPreview={(row) => handleView(row)}
      previewText="Preview"
      columns={TemplateColumns}
      data={data ?? []}
      tableOptions={['search', 'columns', 'filter']}
      id={tableId}
      defaultSort={defaultSort}
      defaultVariables={defaultVariables}
      dataCount={count ?? 0}
      downloadAccessor="template-list"
      search
      error={error}
      filters={filters}
      loading={deleteCompanyTemplateLoading || isLoading || fetchingPresets}
      refetch={refetch}
      tableHeaderProps={
        <PermissionWrapper permissions={['template.create']}>
          <Styled.HeaderButtonStyled
            title="Add Template"
            buttonHeight="40px"
            buttonWidth="124px"
            cy="btnCreateTemplate"
            onClick={() => {
              navigate('new');
            }}
          />
        </PermissionWrapper>
      }
    />
  );
};

TemplatesAll.defaultProps = {};

TemplatesAll.propTypes = {};

export default TemplatesAll;
